<template>
  <div class="audit-detail-page">
    <!-- 顶部导航栏 -->
    <van-nav-bar
      :title="t('auditDetail.title')"
      class="custom-nav-bar"
      :border="false"
    >
      <template #left>
        <Button @click="onClickLeft" type="back" />
      </template>
    </van-nav-bar>

    <!-- 审计详情内容 -->
    <div class="audit-detail-content" v-if="auditDetail">
      <div class="detail-item">
        <div class="detail-label">{{ $t('auditDetail.transactionType') }}</div>
        <div class="detail-value">{{ getTransactionType(auditDetail.type) }}</div>
      </div>

      <div class="detail-item">
        <div class="detail-label">{{ $t('auditDetail.activityName') }}</div>
        <div class="detail-value">{{ getActivityName(auditDetail.type) }}</div>
      </div>

      <div class="detail-item">
        <div class="detail-label">{{ $t('auditDetail.auditStatus') }}</div>
        <div class="detail-value" :class="getAuditStatusInfo(auditDetail.status).class">
          {{ getAuditStatusInfo(auditDetail.status).text }}
        </div>
      </div>

      <div class="detail-item">
        <div class="detail-label">{{ $t('auditDetail.bonusAmount') }}</div>
        <div class="detail-value">{{ VITE_symbol }} {{ numberFormat(auditDetail.bound) }}</div>
      </div>

      <div class="detail-item">
        <div class="detail-label">{{ $t('auditDetail.auditMultiplier') }}</div>
        <div class="detail-value">{{ auditDetail.multiple }}</div>
      </div>

      <div class="detail-item">
        <div class="detail-label">{{ $t('auditDetail.requiredTurnoverAmount') }}</div>
        <div class="detail-value">{{ VITE_symbol }} {{ numberFormat(auditDetail.amount) }}</div>
      </div>

      <div class="detail-item">
        <div class="detail-label">{{ $t('auditDetail.unauditedAmount') }}</div>
        <div class="detail-value">{{ VITE_symbol }} {{ numberFormat(auditDetail.unfinishedAmount) }}</div>
      </div>

      <div class="detail-item">
        <div class="detail-label">{{ $t('auditDetail.auditedAmount') }}</div>
        <div class="detail-value">{{ VITE_symbol }} {{ numberFormat(auditDetail.completeAmount) }}</div>
      </div>
      <div class="detail-item" v-if="auditDetail.gameLimit" @click="goToGameLimit">
        <div class="detail-label">{{ $t('auditDetail.gameLimit') }}</div>
        <div class="detail-value" style="color: #2A6AB4;" >{{ $t('auditDetail.update') }}</div>
      </div>


      <div class="detail-item">
        <div class="detail-label">{{ $t('auditDetail.createTime') }}</div>
        <div class="detail-value">{{ formatDateTime(auditDetail.createTime) }}</div>
      </div>

      <div class="detail-item" >
        <div class="detail-label">{{ $t('auditDetail.completionTime') }}</div>
        <div class="detail-value">{{ formatDateTime(auditDetail.finishTime) }}</div>
      </div>
    </div>

    <!-- 加载中 -->
    <div v-else class="loading-wrap">
      <div class="loading-text">{{ $t('auditDetail.loading') }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from "@/components/Button"
import { useRouter, useRoute } from 'vue-router'
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { numberFormat } from '@/utils/formatter'
import { formatDateTime } from '@/utils/date'
const VITE_symbol   = import.meta.env.VITE_SYMBOL
const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const auditDetail = ref(null)

// 从路由参数中获取审计详情数据
const getAuditDetailFromRoute = () => {
  try {
    const data = route.query.data
    if (data) {
      const decodedData = JSON.parse(decodeURIComponent(data as string))
      auditDetail.value = decodedData
    }
  } catch (error) {
    console.error('解析审计详情数据失败:', error)
  }
}

// 获取交易类型 - 支持多语言翻译
const getTransactionType = (type: number) => {
  return t(`auditDetail.transactionTypes.${type}`) || `Type ${type}`
}

// 获取活动名称的函数 - 使用 activityId 配合翻译枚举
const getActivityName = (activityId: number) => {
  return t(`wallet.chargebackRecord.activityNames.${activityId}`) || `Unknown Activity (${activityId})`
}

// 获取审计状态信息
const getAuditStatusInfo = (status: number) => {
  const auditStatusMap = {
    0: { text: t('wallet.chargebackRecord.auditStatus.pending'), class: 'pending' },
    1: { text: t('wallet.chargebackRecord.auditStatus.processing'), class: 'processing' },
    2: { text: t('wallet.chargebackRecord.auditStatus.approved'), class: 'success' }
  }
  return auditStatusMap[status] || { text: t('wallet.chargebackRecord.auditStatus.unknown'), class: 'timeout' }
}

const onClickLeft = () => {
  // 返回时设置 activeTab 为 audit
  router.push({
    name: 'chargebackRecord',
    query: { tab: 'audit' }
  })
}

onMounted(() => {
  getAuditDetailFromRoute()
})
const goToGameLimit = () => {
  router.push({
    path: '/gameLimit',
    query: {
      drawId: auditDetail.value?.drawId || 0,
      activityId: auditDetail.value?.activityId || 0
    }
  })
}
</script>

<style lang="scss" scoped>
.custom-nav-bar {
  height: 56px;
  box-shadow: 0px 2px 3px 0px rgba(0,0,0,0.3), 0px -2px 22px 0px rgba(53,189,128,0.61);
  &>* {
    height: 100%;
  }
}
.audit-detail-page {
  min-height: 100vh;

  background: #02523d;
  color: #fff;
}

.nav-header {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background: #02523d;
  position: sticky;
  top: 0;
  z-index: 10;
}

.back-icon {
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  margin-right: 20px;
  flex-shrink: 0;
}

.nav-title {
  color: #fff;
  font-size: 16px;
  font-weight: 500;
}

.audit-detail-content {
  padding: 10px 15px;
  background: #00422F;
  margin: 20px;
  border-radius: 8px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #008865;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  color: #34B388;
  font-size: 14px;
  font-weight: 500;
}

.detail-value {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
}

.detail-value.pending {
  color: #FFA500;
}

.detail-value.success {
  color: #27D48B;
}

.detail-value.processing {
  color: #4A90E2;
}

.loading-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.loading-text {
  color: #34B388;
  font-size: 16px;
}

.back-icon {
  color: #fff;
  font-size: 18px;
}
</style>

<route lang="json5">
{
  name: 'auditDetail',
  meta: {
    title: 'Audit Detail',
    i18n: 'menus.auditDetail'
  },
  path: '/wallet/auditDetail'
}
</route>
